import { Field, Form, Formik, FieldArray } from 'formik';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
  CardElement,
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
  Elements,
  injectStripe,
  StripeProvider
} from 'react-stripe-elements';
import { bindActionCreators } from 'redux';
import _actions from '../actions';
import DismissableAlert from '../components/common/DismissableAlert';
import ExpiredPackages from '../components/common/ExpiredPackages';
import { FormField } from '../components/common/FormField';
import { FormFieldWrapper } from '../components/common/FormFieldWrapper';
import { FormHeader } from '../components/common/FormHeader';
import { FormSubheader } from '../components/common/FormSubheader';
import { FormSubmit } from '../components/common/FormSubmit';
import { FormSubmitButton } from '../components/common/FormSubmitButton';
import { FormWrapper } from '../components/common/FormWrapper';
import Price from '../components/common/Price';
import emailRegex from '../utils/emailRegex';
import { addOnsTotals } from '../utils/totals';
import './Checkout.scss';
import ReservationTimer from './ReservationTimer';

const postalCodeLabel = country =>
  country == 'United States' ? 'Zip' : 'Postal Code';
const regionLabel = country =>
  country == 'United States' ? 'State' : 'Region';
const isEmpty = str => str == null || str.trim() == '';
// You can customize your Elements to give it the look and feel of your site.
const createOptions = () => {
  return {
    style: {
      base: {
        'fontSize': '16px',
        'color': '#222',
        'fontFamily': `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
          Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
          'Segoe UI Symbol'`,
        'letterSpacing': '0.025em',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#c23d4b'
      }
    }
  };
};

class _CardForm extends Component {
  state = {
    errorMessage: '',
    cardEmpty: true,
    cardComplete: false
  };

  handleChange = change => {
    if (change.error) {
      this.setState({
        errorMessage: change.error.message,
        cardEmpty: change.empty,
        cardComplete: change.complete
      });
    } else {
      this.setState({
        errorMessage: null,
        cardEmpty: change.empty,
        cardComplete: change.complete
      });
    }
  };

  handleBlur() {
    this.setState({ touched: true });
  }

  getPaymentToken() {
    const { totalAmount } = this.props;
    const skipToken = totalAmount == 0 || this.state.cardEmpty;
    return new Promise((resolve, reject) => {
      skipToken
        ? resolve()
        : this.props.stripe
            .createToken()
            .then(resolve)
            .catch(reject);
    });
  }

  render() {
    const { actions, loading, totalAmount, config } = this.props;

    return (
      <div className="CardDemo">
        <Formik
          initialValues={{
            firstName: '',
            lastName: '',
            email: '',
            confirmEmail: '',
            phone: '',
            street: '',
            city: '',
            country: 'United States',
            region: '',
            postalCode: '',
            giftCards: ['']
          }}
          validate={values => {
            let errors = {};

            if (
              totalAmount > 0 &&
              (this.state.cardEmpty || !this.state.cardComplete) &&
              values.giftCards.filter(gc => gc != null && gc.trim().length > 0)
                .length == 0
            ) {
              errors.giftCards =
                'Please enter a payment method (Card or Gift Card)';
            }

            if (values.firstName == null || values.firstName.trim() == '') {
              errors.firstName = 'First name is required';
            }

            if (values.lastName == null || values.lastName.trim() == '') {
              errors.lastName = 'Last name is required';
            }

            if (values.email == null || !emailRegex.test(values.email.trim())) {
              errors.email = 'Email is required';
            }

            if (values.email != values.confirmEmail) {
              errors.confirmEmail = 'Please confirm your email address';
            }

            if (config.capturePhone) {
              if (values.phone == null || values.phone.trim() == '') {
                errors.phone = 'Phone is required';
              }
            }

            if (config.captureAddress) {
              if (values.street == null || values.street.trim() == '') {
                errors.street = 'Street is required';
              }
              if (values.city == null || values.city.trim() == '') {
                errors.city = 'City is required';
              }
              if (values.country == null || values.country.trim() == '') {
                errors.country = 'Country is required';
              }
              if (values.region == null || values.region.trim() == '') {
                errors.region = `${regionLabel(values.country)} is required`;
              }
              if (values.postalCode == null || values.postalCode.trim() == '') {
                errors.postalCode = `${postalCodeLabel(
                  values.country
                )} is required`;
              }
            }

            return errors;
          }}
          onSubmit={(values, { setSubmitting }) => {
            this.setState({ touched: false }, () => {
              actions.updateSale(values);
              setSubmitting(true);

              this.getPaymentToken()
                .then((result = { token: null }) => {
                  actions
                    .processSale(result.token)
                    .then(() => {
                      actions.transitionTo('/thanks');
                    })
                    .catch(err => {
                      setSubmitting(false);
                      throw err;
                    });
                })
                .catch(err => {
                  setSubmitting(false);
                  throw err;
                });
            });
          }}
        >
          {({ isSubmitting, setFieldValue, values }) => {
            const disabled = isSubmitting || loading;
            return (
              <Form>
                <FormField
                  label="First name"
                  type="text"
                  name="firstName"
                  disabled={disabled}
                  onBlur={(...args) => this.handleBlur(...args)}
                  autoComplete="given-name"
                  maxLength="100"
                  required
                />
                <FormField
                  label="Last name"
                  type="text"
                  name="lastName"
                  disabled={disabled}
                  onBlur={(...args) => this.handleBlur(...args)}
                  autoComplete="family-name"
                  maxLength="100"
                  required
                />
                <FormField
                  label="Email"
                  type="email"
                  name="email"
                  disabled={disabled}
                  onBlur={(...args) => this.handleBlur(...args)}
                  autoComplete="email"
                  maxLength="100"
                  required
                />
                <FormField
                  label="Confirm Email"
                  type="email"
                  name="confirmEmail"
                  disabled={disabled}
                  onBlur={(...args) => this.handleBlur(...args)}
                  maxLength="100"
                  required
                />

                {config.capturePhone && (
                  <FormField
                    label="Phone"
                    type="tel"
                    name="phone"
                    disabled={disabled}
                    onBlur={(...args) => this.handleBlur(...args)}
                    autoComplete="tel"
                    maxLength="50"
                    required
                  />
                )}

                {config.captureAddress && (
                  <>
                    <FormFieldWrapper
                      label="Street"
                      type="textarea"
                      name="street"
                      required
                    >
                      <Field
                        name="street"
                        render={({ field, form: { values } }) => {
                          return (
                            <textarea
                              className="swformfield__control"
                              value={values.street}
                              onChange={e => {
                                let val = e.target.value;
                                val = val.replace(
                                  /([^\r\n]*[\r]?[\n][^\r\n]*)([\s\S]*)/m,
                                  '$1'
                                );
                                setFieldValue(field.name, val);
                              }}
                              onBlur={(...args) => this.handleBlur(...args)}
                              disabled={disabled}
                              autoComplete="steet-address"
                              rows="2"
                            />
                          );
                        }}
                      />
                    </FormFieldWrapper>

                    <FormField
                      label="City"
                      type="text"
                      name="city"
                      disabled={disabled}
                      onBlur={(...args) => this.handleBlur(...args)}
                      autoComplete="address-level2"
                      maxLength="50"
                      required
                    />

                    <FormFieldWrapper
                      label="Country"
                      type="select"
                      name="country"
                      required
                    >
                      <Field
                        name="country"
                        render={({ field, form: { values } }) => {
                          return (
                            <CountryDropdown
                              className="swformfield__control"
                              value={values.country}
                              onChange={val => setFieldValue(field.name, val)}
                              onBlur={(...args) => this.handleBlur(...args)}
                              disabled={disabled}
                              autoComplete="country-name"
                            />
                          );
                        }}
                      />
                    </FormFieldWrapper>

                    <FormFieldWrapper
                      label={regionLabel(values.country)}
                      type="select"
                      name="region"
                      required
                    >
                      <Field
                        name="region"
                        render={({ field, form: { values } }) => {
                          return (
                            <RegionDropdown
                              className="swformfield__control"
                              country={values.country}
                              value={values.region}
                              valueType="short"
                              onChange={val => setFieldValue(field.name, val)}
                              onBlur={(...args) => this.handleBlur(...args)}
                              disabled={disabled}
                              defaultOptionLabel={`Select ${regionLabel(
                                values.country
                              )}`}
                              autoComplete="address-level1"
                            />
                          );
                        }}
                      />
                    </FormFieldWrapper>
                  </>
                )}

                {(config.captureAddress || config.captureZip) && (
                  <FormField
                    label={postalCodeLabel(values.country)}
                    type="text"
                    name="postalCode"
                    disabled={disabled}
                    onBlur={(...args) => this.handleBlur(...args)}
                    autoComplete="postal-code"
                    maxLength="25"
                    required
                  />
                )}

                {totalAmount > 0 && (
                  <>
                    <FormFieldWrapper
                      label="Card"
                      name="card"
                      style={{ margin: '2rem 0' }}
                      className={disabled ? 'disabled' : 'enabled'}
                      error={this.state.errorMessage}
                    >
                      <CardNumberElement
                        disabled={disabled}
                        onChange={this.handleChange}
                        {...createOptions()}
                      />

                      <CardExpiryElement
                        disabled={disabled}
                        onChange={this.handleChange}
                        {...createOptions()}
                      />

                      <CardCVCElement
                        disabled={disabled}
                        onChange={this.handleChange}
                        {...createOptions()}
                      />
                    </FormFieldWrapper>

                    <FieldArray
                      name="giftCards"
                      render={arrayHelpers => (
                        <div
                          className="swformfield"
                          style={{ margin: '1rem 0 4rem' }}
                        >
                          {values.giftCards.map((_, index) => (
                            <div
                              key={index}
                              className="swformfieldwrapper"
                              style={{ display: 'flex' }}
                            >
                              <FormField
                                label={`Gift Card #${index + 1}`}
                                type="text"
                                name={`giftCards[${index}].number`}
                                disabled={disabled}
                                onBlur={(...args) => this.handleBlur(...args)}
                                maxLength="25"
                                style={{ flex: 1 }}
                              />

                              {index > 0 ? (
                                <button
                                  className="swformfieldbtn__gc"
                                  style={{
                                    marginTop: '24px',
                                    marginBottom: '18px',
                                    marginLeft: '8px',
                                    alignItems: 'center'
                                  }}
                                  type="button"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  Remove
                                </button>
                              ) : (
                                ''
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            className="swformfieldbtn__gc"
                            onClick={() => arrayHelpers.push({ number: '' })}
                          >
                            Use another gift card
                          </button>
                        </div>
                      )}
                    />

                    {/* <FormField
                      label="Gift Card"
                      type="text"
                      name="giftCard"
                      disabled={disabled}
                      onBlur={(...args) => this.handleBlur(...args)}
                      maxLength="25"
                    /> */}
                  </>
                )}

                <FormSubmit>
                  <FormSubmitButton disabled={disabled}>
                    {totalAmount > 0 ? (
                      <>
                        Pay{' '}
                        <strong>
                          <Price price={totalAmount} />
                        </strong>
                      </>
                    ) : (
                      'Checkout (Free)'
                    )}
                  </FormSubmitButton>
                </FormSubmit>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }
}

_CardForm.propTypes = {
  stripe: PropTypes.object,
  handleResult: PropTypes.func,
  actions: PropTypes.object,
  loading: PropTypes.bool,
  totalAmount: PropTypes.number,
  config: PropTypes.object.isRequired
};

class _PaymentRequestForm extends Component {
  constructor(props) {
    super(props);

    // const paymentRequest = props.stripe.paymentRequest({
    //   country: 'US',
    //   currency: 'usd',
    //   total: {
    //     label: 'Checkout',
    //     amount: 100 // props.totalAmount
    //   },
    //   requestPayerName: true,
    //   requestPayerEmail: true
    // });

    // paymentRequest.on('token', ({ complete, token, ...data }) => {
    //   props.handleResult({ paymentRequest: { token, data } });
    //   complete('success');
    // });

    // paymentRequest.canMakePayment().then(result => {
    //   this.setState({ canMakePayment: !!result });
    // });

    // this.state = {
    //   canMakePayment: false,
    //   paymentRequest
    // };
  }

  render() {
    return <_CardForm {...this.props} />;

    // return this.state.canMakePayment ? (
    //   <div>
    //     <PaymentRequestButtonElement
    //       paymentRequest={this.state.paymentRequest}
    //       className="PaymentRequestButton"
    //       style={{
    //         // For more details on how to style the Payment Request Button, see:
    //         // https://stripe.com/docs/elements/payment-request-button#styling-the-element
    //         paymentRequestButton: {
    //           theme: 'light',
    //           height: '64px'
    //         }
    //       }}
    //     />
    //     <div>or</div>
    //     <_CardForm {...this.props} />
    //   </div>
    // ) : (
    //   <_CardForm {...this.props} />
    // );
  }
}

_PaymentRequestForm.propTypes = {
  stripe: PropTypes.object,
  handleResult: PropTypes.func,
  totalAmount: PropTypes.number.isRequired,
  config: PropTypes.object.isRequired
};

const PaymentRequestForm = injectStripe(_PaymentRequestForm);

export class StripePaymentRequest extends Component {
  constructor(props) {
    super(props);

    this.state = {
      stripe: null
    };
  }

  componentDidMount() {
    try {
      document.getElementById('stripe-script').remove();
    } catch (o_O) {
      // hmm
    }
    const stripeJs = document.createElement('script');
    stripeJs.id = 'stripe-script';
    stripeJs.src = 'https://js.stripe.com/v3/';
    stripeJs.async = true;
    stripeJs.onload = () => {
      this.setState({
        stripe: window.Stripe(this.props.stripePublicKey)
      });
    };
    document.body && document.body.appendChild(stripeJs);
  }

  render() {
    return this.state.stripe ? (
      <StripeProvider stripe={this.state.stripe}>
        <Elements>
          <PaymentRequestForm {...this.props} />
        </Elements>
      </StripeProvider>
    ) : (
      'Loading ...'
    );
  }
}

StripePaymentRequest.propTypes = {
  stripePublicKey: PropTypes.string,
  handleResult: PropTypes.func,
  totalAmount: PropTypes.number.isRequired,
  config: PropTypes.object.isRequired
};

class CheckoutContainer extends Component {
  componentDidMount() {
    const { sale, actions, done, config } = this.props;

    if (sale.id == null) {
      return actions.transitionTo('/');
    }

    if (sale.day == null) {
      return actions.transitionTo('/');
    }

    if (config.guestMods && !sale.packageAddOnGuests) {
      return actions.transitionTo('/');
    }

    if (done) {
      return actions.transitionTo('/thanks');
    }
  }

  render() {
    const { error, loading, config, actions, sale, inventory } = this.props;

    return (
      <FormWrapper>
        <FormHeader>
          <ReservationTimer sale={sale}>Checkout</ReservationTimer>
        </FormHeader>
        <FormSubheader>
          <ExpiredPackages sale={sale} actions={actions} />
        </FormSubheader>

        <StripePaymentRequest
          totalAmount={addOnsTotals(sale, inventory)}
          stripePublicKey={config.stripePublicKey}
          loading={loading}
          actions={actions}
          config={config}
        />
        <DismissableAlert>{error || inventory.error}</DismissableAlert>
      </FormWrapper>
    );
  }
}

CheckoutContainer.propTypes = {
  actions: PropTypes.object,
  params: PropTypes.object,
  router: PropTypes.object,
  route: PropTypes.object,
  sale: PropTypes.object,
  config: PropTypes.object.isRequired,
  inventory: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
  done: PropTypes.bool
};

const mapStateToProps = state => ({
  ...state.sale,
  config: state.config,
  inventory: state.inventory
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(_actions, dispatch)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(CheckoutContainer));
